import { ReadOnlyMemoryStore } from "@dataspecer/core/core";
import { wdIriToNumId } from "./wikidata-entities/wd-entity.js";
import { loadWikidataAssociation, loadWikidataAttribute } from "./wikidata-to-dataspecer-entity-adapters/wd-property-adapter.js";
import { WdOntologyConnector } from "./wikidata-ontology-connector/wd-ontology-connector.js";
import { isWdErrorResponse } from "./wikidata-ontology-connector/api-types/error.js";
import { loadWikidataClass } from "./wikidata-to-dataspecer-entity-adapters/wd-class-adapter.js";
import { WdSparqlEndpointConnector } from "./wikidata-sparql-endpoint-connector/wd-sparql-endpoint-connector.js";
export function isWikidataAdapter(adapter) {
    return Object.hasOwn(adapter, "thisIsWikidataAdapter");
}
export class WikidataAdapter {
    httpFetch;
    iriProvider;
    static ENTITY_URI_REGEXP = new RegExp("^https?://www.wikidata.org/(entity|wiki)/Q[1-9][0-9]*$");
    wdOntologyConnector;
    wdSparqlEndpointConnector;
    // For type narrowing
    thisIsWikidataAdapter = true;
    constructor(httpFetch, baseUrl) {
        this.httpFetch = httpFetch;
        this.wdOntologyConnector = new WdOntologyConnector(this.httpFetch, baseUrl);
        this.wdSparqlEndpointConnector = new WdSparqlEndpointConnector(this.httpFetch);
    }
    setIriProvider(iriProvider) {
        this.iriProvider = iriProvider;
    }
    async search(query) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        const results = [];
        const response = await this.wdOntologyConnector.getSearch(query);
        if (!isWdErrorResponse(response)) {
            for (const cls of response.classes) {
                results.push(loadWikidataClass(cls, this.iriProvider));
            }
        }
        return results;
    }
    async getClass(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        let result = null;
        if (WikidataAdapter.ENTITY_URI_REGEXP.test(cimIri)) {
            const response = await this.wdOntologyConnector.getSearch(cimIri);
            if (!isWdErrorResponse(response) && response.classes.length === 1) {
                const cls = response.classes[0];
                result = loadWikidataClass(cls, this.iriProvider);
            }
        }
        return result;
    }
    async getFullHierarchy(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        if (WikidataAdapter.ENTITY_URI_REGEXP.test(cimIri)) {
            const response = await this.wdOntologyConnector.getClassHierarchy(wdIriToNumId(cimIri), "full");
            if (!isWdErrorResponse(response)) {
                const resources = this.loadParentsChildrenHierarchy(response);
                return ReadOnlyMemoryStore.create(resources);
            }
        }
        return ReadOnlyMemoryStore.create({});
    }
    // This method should not be called, the surroundings have different dialog to handle surroundings.
    async getSurroundings(cimIri) {
        if (!this.iriProvider) {
            throw new Error("Missing IRI provider.");
        }
        throw new Error("Not not implemented.");
        // return ReadOnlyMemoryStore.create({});
    }
    async getResourceGroup(cimIri) {
        // Keep as is
        return [];
    }
    loadParentsChildrenHierarchy(response) {
        const resources = {};
        const loadedClassesSet = new Set();
        // Load start class
        this.tryLoadClassesToResources([response.startClassId], resources, loadedClassesSet, response.classesMap);
        // Load parents
        this.tryLoadClassesToResources(response.parentsIds, resources, loadedClassesSet, response.classesMap);
        // Load children
        this.tryLoadClassesToResources(response.childrenIds, resources, loadedClassesSet, response.classesMap);
        return resources;
    }
    tryLoadClassesToResources(classesIds, resources, loadedClassesSet, contextClasses) {
        for (const clsId of classesIds) {
            if (!loadedClassesSet.has(clsId)) {
                loadedClassesSet.add(clsId);
                const cls = contextClasses.get(clsId);
                const newPimClass = loadWikidataClass(cls, this.iriProvider, contextClasses);
                resources[newPimClass.iri] = newPimClass;
            }
        }
    }
    tryLoadClassToResource(cls, resources, loadedClassesSet) {
        if (!loadedClassesSet.has(cls.id)) {
            loadedClassesSet.add(cls.id);
            const newPimClass = loadWikidataClass(cls, this.iriProvider);
            resources[newPimClass.iri] = newPimClass;
        }
    }
    tryLoadAssociationToResource(property, subjectClass, objectClass, isInward, resources, loadedPropertiesSet) {
        const [pimMediate1, pimAssociation, pimMediate2] = loadWikidataAssociation(property, subjectClass, objectClass, isInward, this.iriProvider);
        if (!loadedPropertiesSet.has(pimAssociation.iri)) {
            loadedPropertiesSet.add(pimAssociation.iri);
            resources[pimAssociation.iri] = pimAssociation;
            resources[pimMediate1.iri] = pimMediate1;
            resources[pimMediate2.iri] = pimMediate2;
            return pimAssociation;
        }
        return undefined;
    }
    tryLoadAttributeToResource(property, subjectClass, resources, loadedPropertiesSet) {
        const pimAttribute = loadWikidataAttribute(property, subjectClass, this.iriProvider);
        if (!loadedPropertiesSet.has(pimAttribute.iri)) {
            loadedPropertiesSet.add(pimAttribute.iri);
            resources[pimAttribute.iri] = pimAttribute;
            return pimAttribute;
        }
        return undefined;
    }
}
//# sourceMappingURL=wikidata-adapter.js.map